import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Box } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { useModal } from 'utils/hooks/useModal'
import { useCarrierMembershipApi } from 'apps/fleet-planner/hooks/useCarrierMembershipApi'
import { TopNav, Toast, useToastState } from 'transfix-ui'
import AddTrailerButton from './components/AddTrailerButton'
import AddTrailerModal from './components/AddTrailerModal'
import EquipmentListingFilter from './components/EquipmentListingFilter'
import TrailerList from './components/TrailerList'

const useStyles = makeStyles(theme => ({
  container: {
    padding: '88px 24px 16px 24px',
    [theme.breakpoints.up('md')]: {
      padding: '16px 24px 16px 24px',
    },
  },
  toastLink: {
    marginLeft: 5,
  },
}))

const EquipmentListingPage = () => {
  const classes = useStyles()

  const [selectedCarrierId, setSelectedCarrierId] = useState<number | undefined>()
  const [trailerSearchInput, setTrailerSearchInput] = useState<string | undefined>()
  const [showInactiveAndUnassigned, setShowInactiveAndUnassigned] = useState<boolean>(false)
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const { closeModal, isOpen, openModal } = useModal()
  const {
    getUserMotorCarriers,
    isUserMotorCarriersLoading,
    userMotorCarriers,
  } = useCarrierMembershipApi()

  // Fetch motor carriers for the current user on page mount
  useEffect(() => {
    getUserMotorCarriers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Preselect first carrier on load
  useEffect(() => {
    if (!isUserMotorCarriersLoading && userMotorCarriers.length && !selectedCarrierId) {
      setSelectedCarrierId(userMotorCarriers[0].motorCarrier.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMotorCarriers, isUserMotorCarriersLoading])

  // Wait until carriers are loaded and first item is preselected
  if (isUserMotorCarriersLoading || !selectedCarrierId) {
    return <Spinner />
  }

  const handleSubmitTrailer = (newTrailerId: string | undefined, error?: string) => {
    closeModal()
    if (error) {
      openToast({
        toastType: 'alert',
        toastMessage: 'There was an error creating trailer',
      })
    } else {
      openToast({
        toastType: 'default',
        toastMessage: (
          <div>
            <span>New trailer successfully added</span>
            <Link
              className={classes.toastLink}
              to={`/equipment/trailer/${selectedCarrierId}/${newTrailerId}`}>
              View Trailer
            </Link>
          </div>
        ),
      })
    }
  }

  const availableCarriers = userMotorCarriers.map(item => item.motorCarrier)

  const handleCarrierChange = (newCarrierId: number) => {
    setSelectedCarrierId(newCarrierId)
  }

  const handleSearchChange = (newSearch: string) => {
    setTrailerSearchInput(newSearch)
  }

  const handleInactiveToggleChange = () => {
    setShowInactiveAndUnassigned(prevState => !prevState)
  }

  return (
    <>
      <TopNav title='Trailers'>
        <AddTrailerButton onClick={openModal('addTrailer')} />
      </TopNav>
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      <Box className={classes.container}>
        <Box marginBottom={2}>
          <EquipmentListingFilter
            carriers={availableCarriers}
            onCarrierChange={handleCarrierChange}
            onInactiveToggleChange={handleInactiveToggleChange}
            onSearchChange={handleSearchChange}
            selectedCarrierId={selectedCarrierId}
            showInactiveAndUnassigned={showInactiveAndUnassigned}
            trailerSearchInput={trailerSearchInput}
          />
        </Box>
        {selectedCarrierId && (
          <TrailerList
            selectedCarrierId={selectedCarrierId?.toString()}
            showInactiveAndUnassigned={showInactiveAndUnassigned}
            trailerSearchInput={trailerSearchInput}
          />
        )}
      </Box>
      {isOpen('addTrailer') && (
        <AddTrailerModal
          carriers={availableCarriers}
          onCancel={closeModal}
          onSubmit={handleSubmitTrailer}
          onClose={closeModal}
        />
      )}
    </>
  )
}

export default EquipmentListingPage
